import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export const INTERNAL = ["super_admin", "admin", "recruitment_manager", "recruiter", "sourcer", "interviewer"];
export const STAFF = ["super_admin", "admin", "recruitment_manager", "recruiter", "sourcer"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous
  const [meta, setMeta] = useState(null);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get("/meta");
      setMeta(data);
    } catch (e) {
      setMeta(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (user) loadMeta();
  }, [user, loadMeta]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: apiError(e, "Unable to sign in") };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
    setMeta(null);
  };

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {}
  }, []);

  const can = (module, action = "view") =>
    !!user && ((user.permissions || {})[module] || []).includes(action);

  const has = (...roles) => !!user && roles.includes(user.role);
  const isInternal = !!user && INTERNAL.includes(user.role);
  const isStaff = !!user && STAFF.includes(user.role);

  return (
    <AuthContext.Provider
      value={{ user, setUser, meta, refreshMeta: loadMeta, refreshUser, login, logout, can, has, isInternal, isStaff }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
